import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LineChart, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);

  const sendForgot = async () => {
    setForgotSending(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Se l'email è registrata, riceverai un link per reimpostare la password.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error("Impossibile inviare la richiesta. Riprova.");
    } finally {
      setForgotSending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual panel */}
      <div className="hidden lg:block relative overflow-hidden bg-[#0B0F17]">
        <img
          src="https://images.pexels.com/photos/38933571/pexels-photo-38933571.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Analisi finanziaria"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0F17] via-[#0B0F17]/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3 text-white">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-primary">
              <LineChart className="h-5 w-5" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg">Bilancio CEE</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">XBRL Analyst Pro</div>
            </div>
          </div>
          <div className="text-white max-w-md">
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
              Analisi di bilancio da XBRL, pronta per la banca.
            </h1>
            <p className="mt-4 text-slate-300 text-sm leading-relaxed">
              Carica il file XBRL depositato in Camera di Commercio e ottieni indici di bilancio,
              rendiconto finanziario (OIC 10) e report professionali in PDF ed Excel.
            </p>
          </div>
          <div className="text-xs text-slate-500 font-mono">Portale privato · accesso riservato</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-[0.2em] mb-2">
            <Lock className="h-3.5 w-3.5" /> Accesso Riservato
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Entra nel portale</h2>
          <p className="text-sm text-muted-foreground mt-1">Inserisci le tue credenziali per continuare.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@studio.it" autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" required data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" autoComplete="current-password"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" data-testid="login-submit-btn" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Accedi
            </Button>
            <button type="button" onClick={() => setForgotOpen(true)} data-testid="forgot-password-link"
              className="w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors">
              Password dimenticata?
            </button>
          </form>

          <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Reimposta password</DialogTitle>
                <DialogDescription>Inserisci la tua email: riceverai un link per scegliere una nuova password.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <Label htmlFor="fe">Email</Label>
                <Input id="fe" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="nome@studio.it" data-testid="forgot-email-input" />
              </div>
              <DialogFooter>
                <Button onClick={sendForgot} disabled={forgotSending || !forgotEmail} data-testid="forgot-send-btn">
                  {forgotSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Invia link
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </motion.div>
      </div>
    </div>
  );
}
